import { cn } from "@/lib/utils";
import {
  CrmContactInfoItem,
  CrmEditableContactInfoItem,
} from "@/components/crm/contact-detail-controls";

export interface CrmContactQuickInfoItem {
  key: string;
  label: string;
  value: string;
  editableField?: string;
  isVisible?: boolean;
}

interface CrmContactQuickInfoGridProps {
  items: CrmContactQuickInfoItem[];
  onSave?: (field: string, value: string) => Promise<void> | void;
  className?: string;
}

interface BuildCrmContactQuickInfoItemsInput {
  phone?: string | null;
  lifecycleStage?: string | null;
  leadScore?: number | null;
  ownerLabel?: string | null;
  source?: string | null;
}

export function CrmContactQuickInfoGrid({
  items,
  onSave,
  className,
}: CrmContactQuickInfoGridProps) {
  const visibleItems = items.filter((item) => item.isVisible !== false);

  if (visibleItems.length === 0) {
    return null;
  }

  return (
    <div className={cn("grid grid-cols-2 gap-3 text-sm", className)}>
      {visibleItems.map((item) => (
        item.editableField && onSave ? (
          <CrmEditableContactInfoItem
            key={item.key}
            label={item.label}
            value={item.value}
            field={item.editableField}
            onSave={onSave}
          />
        ) : (
          <CrmContactInfoItem key={item.key} label={item.label} value={item.value} />
        )
      ))}
    </div>
  );
}

export function buildCrmContactQuickInfoItems({
  phone,
  lifecycleStage,
  leadScore,
  ownerLabel,
  source,
}: BuildCrmContactQuickInfoItemsInput): CrmContactQuickInfoItem[] {
  const normalizedStage = normalizeCrmContactQuickInfoValue(lifecycleStage);
  const normalizedOwner = normalizeCrmContactQuickInfoValue(ownerLabel);

  return [
    {
      key: "phone",
      label: "Phone",
      value: normalizeCrmContactQuickInfoValue(phone),
      editableField: "phone",
    },
    {
      key: "stage",
      label: "Stage",
      value: normalizedStage,
      isVisible: normalizedStage.length > 0,
    },
    {
      key: "score",
      label: "Score",
      value: typeof leadScore === "number" && Number.isFinite(leadScore) ? String(leadScore) : "",
      isVisible: typeof leadScore === "number" && Number.isFinite(leadScore),
    },
    {
      key: "owner",
      label: "Owner",
      value: normalizedOwner,
      isVisible: normalizedOwner.length > 0,
    },
    {
      key: "source",
      label: "Source",
      value: normalizeCrmContactQuickInfoValue(source),
      editableField: "source",
    },
  ];
}

function normalizeCrmContactQuickInfoValue(value: string | null | undefined) {
  return value?.trim() ?? "";
}
